import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Modal } from '@vimily/bonjoro-ui-components-v2/Modal';
import {
  UpgradeModalTrigger,
  getSpecificPlasmicPlanCardUids,
  upgradeModalKeysList,
} from '../util';
import cx from 'classnames';
import { useSubscriptions } from '~/App/Modules/NewBilling/Hooks/useSubscription';
import { useCustomerCoupon } from '~/App/Api/Coupon';
import { usePlasmicCMS, usePlasmicUpgradeModal } from '~/App/Api/Plasmic';
import {
  PlanCard,
  PlasmicModelType,
  UpgradeModalType,
} from '~/types/models/plasmic';
import { useMappedPlanCards } from '~/App/Modules/NewBilling/Hooks/useMappedPlanCards';
import { PricingPlanCards } from '~/App/Modules/NewBilling/Components/Pricing/PricingPlanCards';
import { PricingPlanCardLayout } from '~/App/Modules/NewBilling/Components/Pricing/PricingPlanCards/PricingPlanCard';
import { BillingCycleToggler } from '~/App/Modules/NewBilling/Components/Common/BillingCycleToggler';
import SeeAllInclusionsLink, {
  ElementType,
} from '~/App/Modules/NewBilling/Components/Common/SeeAllInclusionsLink';
import {
  canSwitchBillingCycle as canSwitchBillingCycleChecker,
  useTmPlan,
  useVmPlan,
} from '~/react-hooks/use-plans';
import { BillingCycle, PaymentGateway, Service } from '~/types/models';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import useCurrentTeam from '~/react-hooks/use-current-team';
import { useAddOnsPrices } from '~/App/Modules/NewBilling/Hooks/useAddOnsPrice';
import { AddOnType } from '~/types/models/addon';
import { RollupLimitAddOnCard } from '~/App/Modules/NewBilling/Components/Subscription/AvailableAddOns/RollUpLimitAddOn/RollUpLimitAddOnCard';
import { mapAddOnToAddOnItem } from '~/App/Modules/NewBilling/utils';
import { ShopifyUpgradeModal } from '../../ShopifyUpgradeModal';
import { ShopifyVideoMessagingPlanSlugs } from '~/types/plans';
import { bonjoroBillingCycleToPlandalfBillingCycleCode } from '~/utils/plandalf';

export interface UpgradeModalProps {
  trigger: UpgradeModalTrigger;
  open?: boolean;
  onCloseClick?: () => void;
  onPlanSelect?: (planSlug: string, planCard: PlanCard) => void;
  withAddOn?: boolean;
  note?: ReactNode;
}

export function UpgradeModal({
  trigger,
  open,
  onCloseClick,
  onPlanSelect,
  withAddOn = false,
  note,
}: UpgradeModalProps) {
  const plandalf = window.plandalf;
  const currentVmPlan = useVmPlan();
  const currentTmPlan = useTmPlan();
  const { payment_gateway } = useCurrentTeam();

  const { billing_cycle: teamBillingCycle } = useCurrentTeam();

  const [billingCycle, setBillingCycle] =
    useState<BillingCycle>(teamBillingCycle);

  const { upgradeModalKeys, currentPlanSlug } = useMemo(() => {
    if (!trigger) return {};

    const upgradeModalKeys = upgradeModalKeysList[trigger];

    return {
      upgradeModalKeys,
      currentPlanSlug:
        upgradeModalKeys?.service === Service.VideoMessaging
          ? currentVmPlan?.slug
          : upgradeModalKeys?.service === Service.Testimonials
          ? currentTmPlan?.slug
          : undefined,
    };
  }, [trigger, currentVmPlan, currentTmPlan]);

  const { data: upgradeModal } = usePlasmicUpgradeModal({
    uid: upgradeModalKeys?.upgradeModalUid,
  });

  const { data: planCards } = usePlasmicCMS<PlanCard>({
    model: PlasmicModelType.PlanCard,
    options: {
      enabled: trigger != undefined && upgradeModalKeys != undefined,
    },
  });

  const { data: subscriptions } = useSubscriptions({});
  const { data: addOnPrices } = useAddOnsPrices({});
  const { data: coupon } = useCustomerCoupon({});

  const handlePlanSelection = (planSlug: string, planCard: PlanCard) => {
    plandalf.intel?.action('comparison', {
      element: 'upgrade-today-modal',
      renew_interval:
        bonjoroBillingCycleToPlandalfBillingCycleCode(billingCycle),
      currency: 'USD',
      selection: [{ type: 'plan', id: planSlug }],
      flow: 'upgrade-button',
    });

    onPlanSelect?.(planSlug, planCard);
  };

  const mappedPlanCards = useMappedPlanCards({
    plasmicPlanCards: planCards?.filter(
      (planCard) =>
        getSpecificPlasmicPlanCardUids({
          trigger,
          planSlug: currentPlanSlug,
        })?.includes(planCard?.uid) &&
        planCard.service === upgradeModalKeys?.service
    ),
    coupon: coupon?.coupon,
    subscriptions,
    billingCycle,
    onPlanSelect: handlePlanSelection,
  });

  const isSplitView = upgradeModal?.type === UpgradeModalType.Split;
  const isWholeView = upgradeModal?.type === UpgradeModalType.Whole;
  const hasOnePlanCard = mappedPlanCards?.length === 1;

  const canSwitchBillingCycle = canSwitchBillingCycleChecker(
    upgradeModalKeys?.service
  );

  const addOnCardComponent = useMemo(() => {
    if (upgradeModal?.relatedAddOn === AddOnType.LimitGreetRollUp) {
      const vmSubscription = subscriptions
        ? subscriptions?.find(
            (subscription) =>
              subscription.service == Service.VideoMessaging ||
              subscription.service == null
          )
        : null;

      if (vmSubscription) {
        const addOns = mapAddOnToAddOnItem(
          vmSubscription?.addons,
          vmSubscription.service
        );

        const rollupLimitAddOnPrice = addOnPrices?.find(
          (item) => item.type === AddOnType.LimitGreetRollUp
        );

        const rollupLimitAddOn = addOns?.find(
          (item) => item.type === AddOnType.LimitGreetRollUp
        );
        return (
          <RollupLimitAddOnCard
            price={rollupLimitAddOnPrice}
            details={rollupLimitAddOn}
            billingCycle={billingCycle}
            className='!w-[480px] !gap-y-5 v2-animate-fade-in-down-150'
            descriptionClassName='text-left !h-fit'
            buttonContainerClassName='!flex-col'
          />
        );
      }
    }
  }, [upgradeModal, subscriptions]);

  const hasPlanCards = mappedPlanCards && mappedPlanCards?.length > 0;
  const showAddOnCard = withAddOn && upgradeModal?.relatedAddOn != undefined;

  const handleCloseModal = () => {
    plandalf.intel?.action('cancel', {
      type: 'close',
      element: 'upgrade-today-modal-close-button',
      flow: 'upgrade-button',
    });

    onCloseClick?.();
  };

  useEffect(() => {
    plandalf.intel?.action('comparison', {
      element: 'upgrade-today-modal',
      renew_interval:
        bonjoroBillingCycleToPlandalfBillingCycleCode(billingCycle),
      currency: 'USD',
      flow: 'upgrade-button',
    });
  }, [billingCycle]);

  if (!upgradeModal || !planCards) return <></>;

  const headingComponent = (
    <div
      className={cx('flex flex-col flex-wrap gap-y-4', {
        'text-left': withAddOn,
      })}
    >
      <span className='text-[26px] font-bold leading-8'>
        {upgradeModal?.header}
      </span>
      {upgradeModal?.subHeader && (
        <span className='text-base font-bold'>{upgradeModal?.subHeader}</span>
      )}
    </div>
  );

  const billingCycleTogglerComponent = canSwitchBillingCycle && (
    <BillingCycleToggler
      billingCycle={billingCycle}
      setBillingCycle={setBillingCycle}
    />
  );

  const seeAllInclusionsLinkComponent = (
    <SeeAllInclusionsLink
      className={cx({
        'md:absolute md:top-0 md:right-0 p-0': isWholeView,
        '!m-auto': isSplitView,
      })}
      elementType={isWholeView ? ElementType.Button : ElementType.Span}
      trackingContext='upgrade modals'
      service={upgradeModalKeys?.service}
    />
  );

  const planCardsComponent =
    mappedPlanCards && mappedPlanCards?.length > 0 ? (
      <PricingPlanCards
        pricingPlanCards={mappedPlanCards}
        discounted={coupon?.coupon != undefined}
        layout={
          hasOnePlanCard
            ? PricingPlanCardLayout.Landscape
            : PricingPlanCardLayout.Portrait
        }
        ctaVariant={hasOnePlanCard ? ButtonVariant.Primary : undefined}
        billingCycle={billingCycle}
        className='flex-wrap'
      />
    ) : (
      <div className='w-full h-full flex items-center justify-center min-h-[480px]'>
        <FontAwesomeIcon className='spinning' icon={faSpinner} />
      </div>
    );

  const containerClassName = cx(
    [
      'text-center flex-1 w-full shrink-0 !p-7',
      'flex flex-col justify-center align-center',
    ],
    {
      'gap-y-8': !showAddOnCard,
      'gap-y-4': showAddOnCard,
    }
  );

  const wholeViewComponent = (
    <div className='flex flex-col lg:flex-row'>
      <div className={containerClassName}>
        {headingComponent}
        <div className='flex flex-col md:flex-row gap-y-4 w-full relative items-center justify-center'>
          {billingCycleTogglerComponent}
          {note}
          {seeAllInclusionsLinkComponent}
        </div>
        {planCardsComponent}
      </div>
    </div>
  );

  const splitViewWithoutAddOnComponent = (
    <div className='flex flex-col lg:flex-row '>
      <div className={containerClassName}>
        <div className='flex flex-col md:flex-row gap-y-4 w-full relative items-center justify-center'>
          {billingCycleTogglerComponent}
        </div>
        {planCardsComponent}
        {note}
        {seeAllInclusionsLinkComponent}
      </div>
      <div
        className={cx([
          'flex flex-col justify-center align-center w-full text-center',
          'lg:w-[434px] p-5 bg-cover bg-no-repeat bg-center rounded-r gap-y-4',
        ])}
        style={{
          backgroundColor: upgradeModal?.backgroundColor,
        }}
      >
        {headingComponent}
        <div>
          <img
            className='w-full h-auto'
            src={upgradeModal?.contentImage?.url}
            alt={upgradeModal?.header}
          />
        </div>
      </div>
    </div>
  );

  const splitViewWithAddOnComponent = (
    <div className='flex flex-row'>
      <div className={containerClassName}>
        {headingComponent}
        <div className='flex flex-col md:flex-row gap-5 w-full relative items-center justify-center'>
          <div className='flex flex-col gap-y-[15px] justify-center items-center'>
            {billingCycleTogglerComponent}
            {planCardsComponent}
          </div>
          {addOnCardComponent}
        </div>
      </div>
    </div>
  );

  const isShopifyLowerTierPlans = () => {
    return (
      currentPlanSlug != ShopifyVideoMessagingPlanSlugs.GrrrowthMonthly &&
      currentPlanSlug !== ShopifyVideoMessagingPlanSlugs.GrrrowthYearly &&
      currentPlanSlug != ShopifyVideoMessagingPlanSlugs.CompanyMonthly &&
      currentPlanSlug !== ShopifyVideoMessagingPlanSlugs.CompanyYearly
    );
  };

  const showShopifyModal =
    payment_gateway == PaymentGateway.Shopify &&
    isShopifyLowerTierPlans() &&
    (trigger == UpgradeModalTrigger.TeamMemberLimit ||
      trigger == UpgradeModalTrigger.CustomDomain);

  if (showShopifyModal) {
    return (
      <ShopifyUpgradeModal
        open={open}
        onCloseClick={onCloseClick}
        trigger={trigger}
      />
    );
  }

  return (
    <Modal
      open={open}
      onModalClose={handleCloseModal}
      closeButtonClassName='!top-6 !right-6 text-[30px] !w-5'
      className={cx('w-full !inset-px !p-0', {
        'max-w-[1160px]': isWholeView,
        'max-w-[1030px]': isSplitView,
        'h-[735px]': !hasPlanCards,
        'h-auto': hasPlanCards,
      })}
    >
      {isSplitView && !showAddOnCard && splitViewWithoutAddOnComponent}
      {isSplitView && showAddOnCard && splitViewWithAddOnComponent}
      {isWholeView && wholeViewComponent}
    </Modal>
  );
}
